import { createTheme } from '@mui/material/styles';

const typographyColor = 'var(--pp-text-primary)';
const secondaryTypographyColor = 'var(--pp-text-secondary)';

const lightTokens = {
  bg: '#ffffff',
  surface: '#ffffff',
  surfaceSoft: '#fafafa',
  surfaceMuted: '#f5f5f5',
  surfaceElevated: '#ffffff',
  inputBg: '#ffffff',
  textPrimary: '#111111',
  textSecondary: '#555555',
  textTertiary: '#777777',
  textMuted: '#888888',
  textDisabled: '#999999',
  border: '#dedede',
  borderStrong: '#cfcfcf',
  hover: 'rgba(17, 17, 17, 0.06)',
  selected: 'rgba(241, 100, 56, 0.12)',
  primaryBg: '#111111',
  primaryFg: '#ffffff',
};

const darkTokens = {
  bg: '#11100f',
  surface: '#181716',
  surfaceSoft: '#201f1d',
  surfaceMuted: '#242220',
  surfaceElevated: '#242220',
  inputBg: '#161514',
  textPrimary: '#f4f0ec',
  textSecondary: '#c7c0ba',
  textTertiary: '#aaa19a',
  textMuted: '#918982',
  textDisabled: '#756e68',
  border: '#34302d',
  borderStrong: '#4a443f',
  hover: 'rgba(244, 240, 236, 0.08)',
  selected: 'rgba(241, 100, 56, 0.18)',
  primaryBg: '#f4f0ec',
  primaryFg: '#11100f',
};

const createPostpopTheme = (mode = 'light') => {
  const tokens = mode === 'dark' ? darkTokens : lightTokens;

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 768,
        lg: 1280,
        xl: 1536,
      },
    },
    palette: {
      mode,
      primary: {
        main: tokens.primaryBg,
        contrastText: tokens.primaryFg,
      },
      secondary: {
        main: '#f16438',
        contrastText: '#ffffff',
      },
      background: {
        default: tokens.bg,
        paper: tokens.surface,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
        disabled: tokens.textDisabled,
      },
      action: {
        hover: tokens.hover,
        selected: tokens.selected,
        disabled: tokens.textDisabled,
        disabledBackground: tokens.surfaceMuted,
      },
      divider: tokens.border,
      grey: {
        50: tokens.surface,
        100: tokens.surfaceSoft,
        200: tokens.surfaceMuted,
        300: tokens.borderStrong,
        500: tokens.textSecondary,
        900: tokens.textPrimary,
        line: 'var(--pp-border)',
        bg: 'var(--pp-surface-muted)',
      },
      black: {
        1: 'var(--pp-text-disabled)',
        2: 'var(--pp-text-muted)',
        3: 'var(--pp-text-tertiary)',
        4: 'var(--pp-text-secondary)',
        5: 'var(--pp-text-secondary)',
        6: 'var(--pp-text-secondary)',
        7: 'var(--pp-text-primary)',
        8: 'var(--pp-text-primary)',
        9: 'var(--pp-text-primary)',
      },
      red: '#d3240f',
      white: {
        main: 'var(--pp-surface)',
        opacity80: 'rgba(255, 255, 255, 0.8)',
        opacity50: 'rgba(255, 255, 255, 0.5)',
      },
      surface: {
        main: 'var(--pp-surface)',
        soft: 'var(--pp-surface-soft)',
        muted: 'var(--pp-surface-muted)',
        elevated: 'var(--pp-surface-elevated)',
      },
    },
    typography: {
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      h1: {
        fontSize: '28px',
        fontWeight: 700,
        color: typographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      h2: {
        fontSize: '24px',
        fontWeight: 400,
        color: secondaryTypographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      h3: {
        fontSize: '22px',
        fontWeight: 500,
        color: typographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      h4: {
        fontSize: '20px',
        fontWeight: 400,
        color: secondaryTypographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      h5: {
        fontSize: '18px',
        fontWeight: 400,
        color: secondaryTypographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      h6: {
        fontSize: '16px',
        fontWeight: 700,
        color: typographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      subtitle1: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#f16438',
        lineHeight: 1,
        letterSpacing: '0',
      },
      title1: {
        fontSize: '28px',
        fontWeight: 'regular',
        color: typographyColor,
        llineHeight: 1,
        letterSpacing: '0',
      },
      title2: {
        fontSize: '24px',
        fontWeight: 'regular',
        color: typographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      body1: {
        fontSize: '16px',
        fontWeight: 'regular',
        color: typographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      body2: {
        fontSize: '15px',
        fontWeight: 'regular',
        color: typographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      body3: {
        fontSize: '14px',
        fontWeight: 'regular',
        color: typographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      body4: {
        fontSize: '13px',
        fontWeight: 'regular',
        color: typographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
      body5: {
        fontSize: '12px',
        fontWeight: 'regular',
        color: typographyColor,
        lineHeight: 1,
        letterSpacing: '0',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: 'var(--pp-bg)',
            color: 'var(--pp-text-primary)',
            transition: 'background-color 160ms ease, color 160ms ease',
          },
          '::selection': {
            backgroundColor: 'rgba(241, 100, 56, 0.28)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--pp-surface-elevated)',
            color: 'var(--pp-text-primary)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--pp-surface-elevated)',
            color: 'var(--pp-text-primary)',
            border: '1px solid var(--pp-border)',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--pp-surface-elevated)',
            color: 'var(--pp-text-primary)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'var(--pp-input-bg)',
              color: 'var(--pp-text-primary)',
              '& fieldset': {
                borderColor: 'var(--pp-border)',
              },
              '&:hover fieldset': {
                borderColor: 'var(--pp-border-strong)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#f16438',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'var(--pp-text-secondary)',
            },
            '& .MuiInputBase-input': {
              color: 'var(--pp-text-primary)',
            },
          },
        },
      },
    },
  });
};

const theme = createPostpopTheme('light');

export { createPostpopTheme };
export default theme;
